/**
 * SceneList — the v1 scenes of an asset as a vertical list (owner request 2026-10-13: no grids).
 *
 * Reads the real analysis output (api.analysis.queries.getSceneSegments + getKeyframes), one row per scene with
 * its keyframe thumbnail and time range. Shared by the asset page's "Escenas" tab (the fallback of FramesTab for
 * an asset without a v2 run) and the Analyse screen. Honest states: undefined -> skeleton, [] -> empty state with
 * the link to start an analysis, rows -> the list.
 */
import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { Btn, EmptyState, Icon, SkeletonList } from '../../components/primitives';
import { fmtClock, fmtDur } from '../../lib/format';
import { navigate } from '../../lib/nav';

const isConvexId = (s: unknown): s is string => typeof s === 'string' && /^[a-z0-9]{20,}$/i.test(s);

export function SceneList({ assetId }: { assetId?: string }) {
  const scenes = useQuery(
    api.analysis.queries.getSceneSegments,
    isConvexId(assetId) ? { assetId: assetId as any } : 'skip',
  ) as Array<{ index: number; startTimeNs: bigint; endTimeNs: bigint; durationMs: number }> | undefined;
  const keyframes = useQuery(
    api.analysis.queries.getKeyframes,
    isConvexId(assetId) ? { assetId: assetId as any } : 'skip',
  ) as Array<{ sceneIndex: number; url?: string | null }> | undefined;

  if (scenes === undefined || keyframes === undefined) return <SkeletonList rows={6} />;
  if (scenes.length === 0) {
    return (
      <EmptyState
        icon="film"
        title="Sin escenas detectadas"
        body="Inicia un análisis para detectar escenas, fotogramas clave y segmentos de audio."
        action={assetId ? <Btn variant="solid" icon="sparkles" onClick={() => navigate('/library/' + assetId + '/analyse')}>Analizar video</Btn> : undefined}
      />
    );
  }
  const kfByScene = new Map<number, { url?: string | null }>();
  for (const kf of keyframes) kfByScene.set(kf.sceneIndex, kf);

  return (
    <ul data-testid="scene-list" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {scenes.map((s) => {
        const kf = kfByScene.get(s.index);
        const startMs = Number(s.startTimeNs / 1_000_000n);
        const endMs = Number(s.endTimeNs / 1_000_000n);
        return (
          <li key={s.index} data-testid="scene-row" style={{ display: 'flex', gap: 12, alignItems: 'center', border: '1px solid var(--border-cool)', borderRadius: 10, padding: 8, background: 'var(--lightest-gray)', color: 'var(--ink)' }}>
            <div style={{ width: 160, aspectRatio: '16 / 9', borderRadius: 8, overflow: 'hidden', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {kf?.url
                ? <img src={kf.url} alt={`Escena ${s.index + 1}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Icon name="film" size={22} color="var(--muted-slate)" />}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Escena {s.index + 1}</div>
              <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginTop: 2 }}>
                {fmtClock(startMs)}–{fmtClock(endMs)} · {fmtDur(s.durationMs)}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
